import React, { useCallback, useState } from 'react';
import {
  View,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector } from '@/store';
import { useGetTagsQuery } from '@/store/api/corkBoard/corkBoardApi';
import { useThreadForm } from '@/hooks/residents/corkBoard/useThreadForm';
import { CreateThreadForm } from '@/components/residents/corkBoard/CreateThreadForm';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

type NavigationProp = StackNavigationProp<RootStackParamList, 'CreateThread'>;

export const CreateThreadScreen: React.FC = () => {
  const { colors, semantic, components } = useThemeColors();
  const navigation = useNavigation<NavigationProp>();
  const { layoutMode } = useResponsive();
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = user?.propertyId;

  const isDesktop = layoutMode === 'desktop';

  const [isDiscardVisible, setIsDiscardVisible] = useState(false);

  const { data: tags = [], isLoading: isLoadingTags } = useGetTagsQuery(
    { propertyId: propertyId ?? 0 },
    { skip: !propertyId },
  );

  const handleSuccess = useCallback((threadId: string) => {
    if (isDesktop) {
      // CreateThread is a transparentModal with animationEnabled: false on
      // desktop, so a single `replace` swaps the transparent modal for the
      // opaque ThreadDetail card in the same frame and paints both briefly.
      // Dismiss the modal first, then push the detail screen on the next
      // frame so each transition is visually atomic.
      navigation.goBack();
      requestAnimationFrame(() => {
        navigation.navigate('ThreadDetail', { threadId });
      });
    } else {
      navigation.replace('ThreadDetail', { threadId });
    }
  }, [isDesktop, navigation]);

  const form = useThreadForm({
    mode: 'create',
    onSuccess: handleSuccess,
  });

  const canSubmit = form.isValid;

  const handleClose = useCallback(() => {
    if (form.isDirty) {
      setIsDiscardVisible(true);
    } else {
      navigation.goBack();
    }
  }, [form.isDirty, navigation]);

  const handleDiscard = useCallback(() => {
    setIsDiscardVisible(false);
    navigation.goBack();
  }, [navigation]);

  return (
    <>
      {isDesktop && (
        <TouchableOpacity
          style={styles.desktopBackdrop}
          activeOpacity={1}
          onPress={handleClose}
        />
      )}

      <UnifiedModal
        visible={true}
        variant={isDesktop ? 'dialog' : 'fullscreen'}
        animateEntry={false}
        onClose={handleClose}
        showHeader={false}
        scrollable={false}
        avoidKeyboard
      >
        <View style={[styles.header, { borderBottomColor: colors.card.border }]}>
          <TouchableOpacity
            style={styles.headerSide}
            onPress={handleClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <RESIDENTS_SVG_COMPONENTS.CANCEL_X width={18} height={18} color={semantic.text.primary} />
          </TouchableOpacity>

          <Text style={[styles.headerTitle, { color: semantic.text.primary }]}>
            New Post
          </Text>

          <TouchableOpacity
            style={[styles.headerSide, styles.postButton, { backgroundColor: semantic.interactive.primary }, !canSubmit && styles.postButtonDisabled]}
            onPress={form.handleSubmit}
            disabled={!canSubmit || form.isSubmitting}
            activeOpacity={0.7}
          >
            {form.isSubmitting ? (
              <ActivityIndicator size="small" color={components.button.primaryText} />
            ) : (
              <Text style={[styles.postButtonText, { color: components.button.primaryText }]}>
                Post
              </Text>
            )}
          </TouchableOpacity>
        </View>

        {isLoadingTags ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={semantic.interactive.primary} />
          </View>
        ) : (
          <CreateThreadForm
            mode="create"
            tags={tags}
            title={form.title}
            onChangeTitle={form.setTitle}
            body={form.body}
            onChangeBody={form.setBody}
            selectedTagIds={form.selectedTagIds}
            onToggleTag={form.toggleTag}
            attachments={form.attachments}
            existingAttachments={form.existingAttachments}
            onAddFiles={form.addFiles}
            onRemoveFile={form.removeFile}
            onRemoveExistingAttachment={form.removeExistingAttachment}
            errors={form.errors}
            hasAttemptedSubmit={form.hasAttemptedSubmit}
            isSubmitting={form.isSubmitting}
            uploadProgress={form.uploadProgress}
            submitError={form.submitError}
          />
        )}
      </UnifiedModal>

      <ConfirmationModal
        visible={isDiscardVisible}
        message="You have unsaved changes. Are you sure you want to discard this post?"
        onCancel={() => setIsDiscardVisible(false)}
        onProceed={handleDiscard}
        cancelText="Keep Editing"
        proceedText="Discard"
      />
    </>
  );
};

const styles = StyleSheet.create({
  desktopBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerSide: {
    minWidth: 60,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '600',
    textAlign: 'center',
    flex: 1,
  },
  postButton: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 34,
  },
  postButtonDisabled: {
    opacity: 0.4,
  },
  postButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
